import { useMemo } from "react";

import { useAtom } from "jotai";

import { useLocation } from "@redwoodjs/router";

import { settingsDialogAtom } from "src/components/SettingsDialog/SettingsDialog";
import { Button } from "src/components/ui/button";
import { storeIntegrationTypes } from "src/lib/constants";
import { useIntegration } from "src/lib/hooks/useIntegration";

const getRelevantTypesForPath = (pathname) => {
  if (pathname.startsWith("/marketing/meta")) {
    return new Set(["META_ADS"]);
  }
  if (pathname.startsWith("/marketing/google")) {
    return new Set(["GOOGLE_ADS"]);
  }
  if (pathname.startsWith("/marketing")) {
    return null;
  }
  return new Set();
};

export function MarketingIntegrationNotice() {
  const { pathname } = useLocation();
  const relevantTypes = getRelevantTypesForPath(pathname);
  const { selectedIntegration: store } = useIntegration(
    storeIntegrationTypes.shopify,
    {
      queryOnRender: false,
    }
  );
  const [, setSettingsDialogState] = useAtom(settingsDialogAtom);

  const failingIntegrations = useMemo(() => {
    const integrations =
      store?.children?.filter(
        (integration) =>
          integration.activeIssue || integration.credentialStatus?.expiresSoon
      ) || [];

    if (relevantTypes === null) {
      return integrations;
    }

    return integrations.filter((integration) =>
      relevantTypes.has(integration.type)
    );
  }, [relevantTypes, store]);

  if (!failingIntegrations.length) {
    return null;
  }

  const primaryIntegration = failingIntegrations[0];

  return (
    <div className="border-destructive/30 bg-destructive/5 rounded-xl border px-4 py-3">
      <div className="text-sm">
        {failingIntegrations.map((integration) => (
          <p key={integration.id}>
            {integration.description || integration.type}:{" "}
            {integration.credentialStatus?.awaitingSync
              ? "Connection verified; data catch-up is pending."
              : integration.activeIssue
                ? "Data sync needs attention."
                : "Access expires soon; reconnect to keep syncing."}
            {integration.lastSyncedAt &&
              ` Last successful pull: ${new Date(integration.lastSyncedAt).toLocaleString()}.`}
          </p>
        ))}
      </div>
      <Button
        variant="outline"
        size="sm"
        className="mt-3"
        onClick={() => {
          setSettingsDialogState((prev) => ({
            ...prev,
            open: true,
            activeTab: "store",
            manageParam: primaryIntegration.type.toLowerCase(),
            sectionTarget: "integrations",
            sectionTargetNonce: Date.now(),
          }));
        }}
      >
        Open quick fix
      </Button>
    </div>
  );
}
